import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { LocationOn, People, Public } from '@mui/icons-material';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { studioRequest } from '../../api/studioClient';

const POPULAR_COUNTRIES = [
  { code: '', label: 'All Countries' },
  { code: 'IN', label: '🇮🇳 India' },
  { code: 'US', label: '🇺🇸 USA' },
  { code: 'GB', label: '🇬🇧 UK' },
  { code: 'CA', label: '🇨🇦 Canada' },
  { code: 'AU', label: '🇦🇺 Australia' },
  { code: 'AE', label: '🇦🇪 UAE' },
  { code: 'SG', label: '🇸🇬 Singapore' },
  { code: 'NZ', label: '🇳🇿 New Zealand' },
  { code: 'ZA', label: '🇿🇦 South Africa' },
  { code: 'MY', label: '🇲🇾 Malaysia' },
  { code: 'TH', label: '🇹🇭 Thailand' },
  { code: 'ID', label: '🇮🇩 Indonesia' },
  { code: 'PH', label: '🇵🇭 Philippines' },
  { code: 'BD', label: '🇧🇩 Bangladesh' },
  { code: 'SA', label: '🇸🇦 Saudi Arabia' },
  { code: 'QA', label: '🇶🇦 Qatar' },
  { code: 'KW', label: '🇰🇼 Kuwait' },
  { code: 'OM', label: '🇴🇲 Oman' },
  { code: 'DE', label: '🇩🇪 Germany' },
  { code: 'FR', label: '🇫🇷 France' },
  { code: 'NL', label: '🇳🇱 Netherlands' },
  { code: 'IT', label: '🇮🇹 Italy' },
  { code: 'ES', label: '🇪🇸 Spain' },
  { code: 'MU', label: '🇲🇺 Mauritius' },
  { code: 'PK', label: '🇵🇰 Pakistan' },
  { code: 'NP', label: '🇳🇵 Nepal' },
  { code: 'LK', label: '🇱🇰 Sri Lanka' },
];

interface City {
  id: string;
  name: string;
  displayName: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  timezone: {
    name: string;
    offset: number;
  };
  population: number;
  admin1?: {
    name: string;
    code?: string;
  };
  country?: {
    name: string;
    code: string;
  };
}

interface CitySearchProps {
  value: City | null;
  onChange: (city: City | null) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
}

const CitySearch: React.FC<CitySearchProps> = ({
  value,
  onChange,
  label = 'जन्म स्थान / Place of Birth',
  placeholder = 'अपना शहर खोजें / Search your city...',
  error = false,
  helperText = '',
  required = false,
  disabled = false
}) => {
  const { i18n } = useTranslation();
  const isHindi = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi');
  const { defaultCity } = useUserPreferences();
  const [options, setOptions] = useState<City[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [popularCities, setPopularCities] = useState<City[]>([]);
  const [country, setCountry] = useState('IN');
  const [didApplyDefaultCity, setDidApplyDefaultCity] = useState(false);

  useEffect(() => {
    if (didApplyDefaultCity || value || !defaultCity) return;
    onChange(defaultCity as City);
    setInputValue(defaultCity.displayName || defaultCity.name);
    if (defaultCity.country?.code) setCountry(defaultCity.country.code);
    setDidApplyDefaultCity(true);
  }, [defaultCity, didApplyDefaultCity, onChange, value]);

  useEffect(() => {
    if (value) {
      setInputValue(value.displayName || value.name);
    }
  }, [value]);

  // Load popular cities on component mount
  useEffect(() => {
    const loadPopularCities = async () => {
      try {
        const data = await studioRequest<{ locations?: City[]; cities?: City[]; items?: City[] }>({
          url: '/locations/popular',
          method: 'GET',
          params: { limit: 15 }
        }, 'Failed to load popular cities');
        const cities = data.locations || data.cities || data.items || [];
        setPopularCities(cities);
        setOptions(cities);
      } catch (error) {
        console.error('Error loading popular cities:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadPopularCities();
  }, []);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce(async (query: string, countryCode: string) => {
      if (query.length < 2) {
        setOptions(popularCities);
        setLoading(false);
        return;
      }

      try {
        const data = await studioRequest<{ locations?: City[]; cities?: City[]; items?: City[] }>({
          url: '/locations/search',
          method: 'GET',
          params: {
            q: query,
            limit: 10,
            ...(countryCode ? { country: countryCode } : {})
          }
        }, 'City search failed');
        setOptions(data.locations || data.cities || data.items || []);
      } catch (error) {
        console.error('City search error:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [popularCities]
  );

  useEffect(() => {
    const normalizedInput = inputValue.trim();
    const selectedLabel = value ? (value.displayName || value.name) : '';

    if (!normalizedInput || normalizedInput.length < 2 || normalizedInput === selectedLabel) {
      debouncedSearch.cancel();
      setLoading(false);
      if (!normalizedInput) setOptions(popularCities);
      return;
    }

    setLoading(true);
    debouncedSearch(normalizedInput, country);
    
    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, debouncedSearch, value, country, popularCities]);

  const formatPopulation = (population: number) => {
    if (population >= 10000000) {
      return `${(population / 10000000).toFixed(1)}Cr`;
    } else if (population >= 100000) {
      return `${(population / 100000).toFixed(1)}L`;
    } else if (population >= 1000) {
      return `${(population / 1000).toFixed(1)}K`;
    }
    return population.toString();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Country selector */}
      <FormControl size="small" fullWidth>
        <InputLabel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Public fontSize="small" /> Country
          </Box>
        </InputLabel>
        <Select
          value={country}
          label="Country"
          onChange={(e) => {
            setCountry(e.target.value);
            setInputValue('');
            setOptions(popularCities);
            setLoading(false);
            onChange(null);
          }}
        >
          {POPULAR_COUNTRIES.map((c) => (
            <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Autocomplete
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      loadingText="खोज रहे हैं... / Searching..."
      noOptionsText={inputValue.length < 2 
        ? "शहर का नाम टाइप करें / Type city name"
        : "कोई शहर नहीं मिला / No cities found"
      }
      getOptionLabel={(option) => option.displayName || option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x} // Disable built-in filtering since we're doing server-side search
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText || (!defaultCity
            ? (isHindi ? 'Tip: आप Settings में default city save कर सकते हैं।' : 'Tip: You can save a default city in Settings.')
            : '')}
          required={required}
          InputProps={{
            ...params.InputProps,
            startAdornment: <LocationOn sx={{ color: 'text.secondary', mr: 1 }} />,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={16} />}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <LocationOn sx={{ color: 'text.secondary', mr: 1.5 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.name}
              </Typography>
              {option.name !== option.displayName && (
                <Typography variant="caption" color="text.secondary">
                  {option.displayName}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {option.coordinates.latitude.toFixed(4)}°, {option.coordinates.longitude.toFixed(4)}° • {option.timezone.name}
              </Typography>
            </Box>
            {option.population > 0 && (
              <Chip
                icon={<People />}
                label={formatPopulation(option.population)}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            variant="outlined"
            label={option.displayName}
            size="small"
          />
        ))
      }
      sx={{
        '& .MuiOutlinedInput-root': {
          paddingLeft: 0.5
        }
      }}
    />
    </Box>
  );
};

export default CitySearch;
